import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_KIOSK_TIMEOUT_MS, fetchKioskTimeoutMs } from '../api/kioskTimeout';
import IdleScreenTimeModal from './modals/IdleScreenTimeModal';
import { clearContribution } from '../utils/contribution';
import { clearTicket } from '../utils/ticket';

const ACTIVITY_DEBOUNCE_MS = 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'click', 'scroll'];

function shouldTrackIdle(pathname) {
  return Boolean(pathname) && pathname !== '/scan';
}

function KioskSessionManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeoutMs, setTimeoutMs] = useState(DEFAULT_KIOSK_TIMEOUT_MS);
  const [loaded, setLoaded] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);

  const idleWarningTimerRef = useRef(null);
  const sessionExpireTimerRef = useRef(null);
  const activityDebounceRef = useRef(null);
  const idleTimeoutMsRef = useRef(timeoutMs);
  const loadedRef = useRef(loaded);
  const pathnameRef = useRef(location.pathname);
  const showIdleModalRef = useRef(false);

  idleTimeoutMsRef.current = timeoutMs;
  loadedRef.current = loaded;
  pathnameRef.current = location.pathname;
  showIdleModalRef.current = showIdleModal;

  useEffect(() => {
    let cancelled = false;
    fetchKioskTimeoutMs().then((next) => {
      if (cancelled) return;
      setTimeoutMs(next > 0 ? next : DEFAULT_KIOSK_TIMEOUT_MS);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const goHome = useCallback(() => {
    setShowIdleModal(false);
    clearTicket();
    clearContribution();
    navigate('/', { replace: true });
  }, [navigate]);

  const goHomeRef = useRef(goHome);
  goHomeRef.current = goHome;

  const clearIdleTimers = useCallback(() => {
    if (idleWarningTimerRef.current) {
      window.clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
    if (sessionExpireTimerRef.current) {
      window.clearTimeout(sessionExpireTimerRef.current);
      sessionExpireTimerRef.current = null;
    }
    if (activityDebounceRef.current) {
      window.clearTimeout(activityDebounceRef.current);
      activityDebounceRef.current = null;
    }
  }, []);

  const startSessionExpireTimer = useCallback(() => {
    if (sessionExpireTimerRef.current) {
      window.clearTimeout(sessionExpireTimerRef.current);
    }
    const expireMs = idleTimeoutMsRef.current;
    if (expireMs <= 0) return;
    sessionExpireTimerRef.current = window.setTimeout(() => {
      goHomeRef.current();
    }, expireMs);
  }, []);

  const showIdleWarning = useCallback(() => {
    setShowIdleModal(true);
    startSessionExpireTimer();
  }, [startSessionExpireTimer]);

  const scheduleIdleWarningTimer = useCallback(() => {
    if (idleWarningTimerRef.current) {
      window.clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
    const warningMs = idleTimeoutMsRef.current;
    if (warningMs <= 0) return;
    idleWarningTimerRef.current = window.setTimeout(() => {
      showIdleWarning();
    }, warningMs);
  }, [showIdleWarning]);

  const resetInactivityTimer = useCallback(() => {
    clearIdleTimers();
    setShowIdleModal(false);
    if (!loadedRef.current || !shouldTrackIdle(pathnameRef.current) || idleTimeoutMsRef.current <= 0) {
      return;
    }
    scheduleIdleWarningTimer();
  }, [clearIdleTimers, scheduleIdleWarningTimer]);

  useEffect(() => {
    if (!loaded) return undefined;
    if (!shouldTrackIdle(location.pathname)) {
      clearIdleTimers();
      setShowIdleModal(false);
      return undefined;
    }
    if (!showIdleModalRef.current) {
      clearIdleTimers();
      scheduleIdleWarningTimer();
    }
    return undefined;
  }, [clearIdleTimers, loaded, location.pathname, scheduleIdleWarningTimer, timeoutMs]);

  useEffect(() => {
    if (!loaded || !shouldTrackIdle(location.pathname)) return undefined;

    const onActivity = () => {
      if (showIdleModalRef.current) return;
      if (activityDebounceRef.current) window.clearTimeout(activityDebounceRef.current);
      activityDebounceRef.current = window.setTimeout(() => {
        resetInactivityTimer();
      }, ACTIVITY_DEBOUNCE_MS);
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [loaded, location.pathname, resetInactivityTimer]);

  useEffect(() => () => clearIdleTimers(), [clearIdleTimers]);

  return (
    <IdleScreenTimeModal
      show={showIdleModal}
      onContinue={resetInactivityTimer}
      onQuit={() => {
        clearIdleTimers();
        goHome();
      }}
    />
  );
}

export default KioskSessionManager;
